import {
  Component,
  inject,
  signal,
  computed,
  input,
  output,
  effect
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { AsesorSugerido } from 'src/app/domain/model/orden-servicio/orden-servicio';
import { OrdenServicioRepository } from 'src/app/domain/service/orden-servicio/orden-servicio-repository';
import { seleccionarAsesorDialogPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * SeleccionarAsesorDialogComponent
 *
 * Diálogo para seleccionar un asesor/consultor al programar una cita
 * cuando el usuario NO es de tipo Asesor.
 *
 * Muestra asesores sugeridos ordenados por prioridad:
 * 1. SUGERIDO: Asesor pre-asignado a la tarea
 * 2. ATENDIÓ: Asesores que ya atendieron al beneficiario
 * 3. OTROS: Otros asesores disponibles
 *
 * Legacy: ScheduleAdvisorViewImpl.java, ScheduleAdvisorActivity.java
 * SP: PCK_PSC_PROGRAMAR_ORDENES.SP_GET_POSIBLES_ASESORES
 *
 * @example
 * ```html
 * <app-seleccionar-asesor-dialog
 *   [visible]="mostrarDialogAsesor()"
 *   [nmconsecutivoTarea]="ordenSeleccionada()?.nmconsecutivo"
 *   (onClose)="cerrarDialogAsesor()"
 *   (onSeleccionar)="onAsesorSeleccionado($event)">
 * </app-seleccionar-asesor-dialog>
 * ```
 *
 * @author GIROP Migration
 * @since 1.0
 */
@Component({
  selector: 'app-seleccionar-asesor-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TableModule,
    TagModule,
    ProgressSpinnerModule,
    MessageModule,
    TooltipModule,
    DatePipe
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [header]="'Seleccionar Consultor'"
      [visible]="visible()"
      [modal]="true"
      [closable]="!cargando()"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '92vw', maxWidth: '44rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      (visibleChange)="onVisibleChange($event)"
    >
      <!-- Loading -->
      @if (cargando()) {
        <div class="flex flex-col items-center justify-center py-8">
          <p-progressSpinner styleClass="w-12 h-12" strokeWidth="4">
          </p-progressSpinner>
          <span class="mt-4 text-gray-600"
            >Consultando consultores disponibles...</span
          >
        </div>
      }

      <!-- Error -->
      @if (error()) {
        <p-message severity="error" [text]="error()!" styleClass="w-full mb-4">
        </p-message>
      }

      <!-- Tabla de asesores -->
      @if (!cargando() && asesores().length > 0) {
        <div class="md:hidden space-y-3 p-3">
          @for (asesor of asesores(); track asesor.nmconsecutivoAsesor) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Nombre
                  </div>
                  <div
                    class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                    [attr.title]="tituloTabla(asesor.nombre)"
                  >
                    {{ textoTabla(asesor.nombre) }}
                  </div>
                </div>
                <p-tag
                  [value]="textoTabla(asesor.dsOrden)"
                  [severity]="getSeverity(asesor)"
                ></p-tag>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Disponible:</span
                  >
                  @if (asesor.fechaDisponible) {
                    <span class="text-gray-800 dark:text-gray-200 ml-1">{{
                      asesor.fechaDisponible | date: 'dd/MM/yyyy HH:mm'
                    }}</span>
                  } @else {
                    <span class="text-gray-400 ml-1">Sin fecha</span>
                  }
                </div>
              </div>
              <button
                pButton
                type="button"
                icon="pi pi-check"
                class="p-button-outlined p-button-success min-h-[44px] w-full"
                label="Seleccionar"
                (click)="seleccionar(asesor)"
              ></button>
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="asesoresTableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="asesores()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [tableStyle]="{
              'min-width': asesoresTableConfig().responsive.minWidth || '44rem'
            }"
            [scrollHeight]="
              asesoresTableConfig().responsive.scrollHeight || '400px'
            "
            [styleClass]="asesoresTableStyleClass()"
            [selectionMode]="asesoresTableSelectionMode()"
            [(selection)]="asesorSeleccionado"
            [dataKey]="asesoresTableDataKey()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Tipo">Tipo</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Nombre">Nombre</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Disponible"
                    >Disponible</span
                  >
                </th>
                <th pResizableColumn style="width: 110px">
                  <span class="grid-header-text" title="Acción">Acción</span>
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-asesor>
              <tr [pSelectableRow]="asesor" class="cursor-pointer">
                <td>
                  <p-tag
                    [value]="textoTabla(asesor.dsOrden)"
                    [severity]="getSeverity(asesor)"
                  >
                  </p-tag>
                </td>
                <td>
                  <span
                    class="grid-cell-text font-medium"
                    [attr.title]="tituloTabla(asesor.nombre)"
                    >{{ textoTabla(asesor.nombre) }}</span
                  >
                </td>
                <td>
                  @if (asesor.fechaDisponible) {
                    <span
                      class="grid-cell-text text-sm"
                      [attr.title]="
                        tituloTabla(
                          (asesor.fechaDisponible | date: 'dd/MM/yyyy HH:mm') ??
                            ''
                        )
                      "
                    >
                      {{ asesor.fechaDisponible | date: 'dd/MM/yyyy HH:mm' }}
                    </span>
                  } @else {
                    <span
                      class="grid-cell-text text-gray-400 text-sm"
                      [attr.title]="tituloTabla('Sin fecha')"
                      >Sin fecha</span
                    >
                  }
                </td>
                <td class="text-center">
                  <a
                    role="button"
                    tabindex="0"
                    class="text-blue-600 hover:underline focus:underline focus:outline-none cursor-pointer"
                    pTooltip="Seleccionar"
                    (click)="seleccionar(asesor)"
                    (keydown.enter)="seleccionar(asesor)"
                    (keydown.space)="seleccionar(asesor); $event.preventDefault()"
                    >Seleccionar</a
                  >
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4" class="text-center py-8">
                  <i class="pi pi-info-circle text-2xl text-gray-400 mb-2"></i>
                  <p class="text-gray-500">
                    No se encontraron consultores disponibles
                  </p>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      }

      <!-- Sin datos -->
      @if (!cargando() && !error() && asesores().length === 0) {
        <div class="flex flex-col items-center justify-center py-8">
          <i class="pi pi-users text-4xl text-gray-300 mb-3"></i>
          <p class="text-gray-500">
            No se encontraron consultores disponibles para esta orden
          </p>
        </div>
      }

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <button
            pButton
            type="button"
            label="Cancelar"
            icon="pi pi-times"
            class="p-button-outlined p-button-secondary"
            [disabled]="cargando()"
            (click)="cerrar()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class SeleccionarAsesorDialogComponent {
  private readonly ordenServicioRepo = inject(OrdenServicioRepository);

  // === Inputs ===

  /** Visibilidad del diálogo */
  readonly visible = input.required<boolean>();

  /** Consecutivo de la tarea/orden para consultar asesores */
  readonly nmconsecutivoTarea = input<number | undefined>();

  // === Outputs ===

  /** Evento al cerrar el diálogo */
  readonly onClose = output<void>();

  /** Evento al seleccionar un asesor */
  readonly onSeleccionar = output<AsesorSugerido>();

  // === State ===

  readonly cargando = signal(false);
  readonly error = signal<string | null>(null);
  readonly asesores = signal<AsesorSugerido[]>([]);

  asesorSeleccionado: AsesorSugerido | null = null;

  readonly asesoresTableConfig = computed(() =>
    seleccionarAsesorDialogPreset.createConfig({
      data: this.asesores() as unknown as Record<string, unknown>[],
      style: {
        tableClassName: 'tabla-busqueda-seleccionar-asesor',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      selection: {
        mode: 'single',
        selected: this.asesorSeleccionado as unknown as Record<
          string,
          unknown
        > | null,
        dataKey: 'nmconsecutivoAsesor'
      },
      state: {
        loading: this.cargando(),
        emptyMessage: 'No se encontraron consultores disponibles'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '44rem',
        scrollHeight: '400px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de consultores sugeridos para seleccion',
        announceSelection: true,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly asesoresTableStyleClass = computed(() => {
    const config = this.asesoresTableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly asesoresTableSelectionMode = computed<'single' | 'multiple' | null>(
    () => {
      const mode = this.asesoresTableConfig().selection.mode;
      return mode === 'single' || mode === 'multiple' ? mode : null;
    }
  );

  readonly asesoresTableDataKey = computed(() =>
    String(
      this.asesoresTableConfig().selection.dataKey || 'nmconsecutivoAsesor'
    )
  );

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  constructor() {
    // Efecto para cargar asesores cuando el diálogo se abre
    effect(() => {
      const isVisible = this.visible();
      const tarea = this.nmconsecutivoTarea();

      if (isVisible && tarea) {
        this.cargarAsesores(tarea);
      } else if (!isVisible) {
        // Limpiar al cerrar
        this.asesores.set([]);
        this.error.set(null);
        this.asesorSeleccionado = null;
      }
    });
  }

  /**
   * Carga los asesores sugeridos para la tarea
   */
  private async cargarAsesores(nmconsecutivoTarea: number): Promise<void> {
    this.cargando.set(true);
    this.error.set(null);
    this.asesores.set([]);
    this.asesorSeleccionado = null;

    try {
      console.log(
        '[SeleccionarAsesor] Consultando asesores para tarea:',
        nmconsecutivoTarea
      );

      const resultado = await this.ordenServicioRepo.consultarAsesoresSugeridos(
        nmconsecutivoTarea,
        'S', // incluirSugerido
        'S', // incluirAtendieron
        'N' // incluirOtros - el backend hace retry si no encuentra
      );

      console.log('[SeleccionarAsesor] Asesores recibidos:', resultado.length);
      this.asesores.set(resultado);

      // Auto-seleccionar el primero si existe
      if (resultado.length > 0) {
        this.asesorSeleccionado = resultado[0];
      }
    } catch (err) {
      console.error('[SeleccionarAsesor] Error consultando asesores:', err);
      this.error.set(
        'Error al consultar consultores disponibles. Intente nuevamente.'
      );
    } finally {
      this.cargando.set(false);
    }
  }

  /**
   * Obtiene la severidad del tag según el tipo de asesor
   */
  getSeverity(asesor: AsesorSugerido): 'success' | 'info' | 'secondary' {
    if (asesor.esSugerido) return 'success';
    if (asesor.yaAtendio) return 'info';
    return 'secondary';
  }

  /**
   * Selecciona un asesor y emite el evento
   */
  seleccionar(asesor: AsesorSugerido): void {
    this.onSeleccionar.emit(asesor);
    this.onClose.emit();
  }

  /**
   * Confirma la selección del asesor seleccionado en la tabla
   */
  confirmarSeleccion(): void {
    if (this.asesorSeleccionado) {
      this.seleccionar(this.asesorSeleccionado);
    }
  }

  /**
   * Cierra el diálogo
   */
  cerrar(): void {
    this.onClose.emit();
  }

  /**
   * Maneja cambios de visibilidad del p-dialog
   */
  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrar();
    }
  }
}
